import React, { useEffect, useState } from "react";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import {
  Calendar,
  ArrowRight,
  Clock,
  Eye,
  MapPin,
  Users,
  Star,
} from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../utils/axios.config";
import { baseUrl } from "../../utils/url";

const RecentActivities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleGetActivities = async () => {
    try {
      setLoading(true);
      // Fetch only recent 8 activities for home page
      const { data } = await api.get(
        "recent-activities/get?limit=8&status=published&sortBy=activityDate&sortOrder=desc"
      );

      if (data.success && data.data.activities.length) {
        setActivities(data?.data?.activities);
      }
    } catch (error) {
      console.error("Failed to fetch activities:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetActivities();
  }, []);

  const responsive = {
    superLargeDesktop: {
      breakpoint: { max: 4000, min: 3000 },
      items: 4,
      partialVisibilityGutter: 40,
    },
    desktop: {
      breakpoint: { max: 3000, min: 1280 },
      items: 3,
      partialVisibilityGutter: 30,
    },
    laptop: {
      breakpoint: { max: 1280, min: 1024 },
      items: 2,
      partialVisibilityGutter: 30,
    },
    tablet: {
      breakpoint: { max: 1024, min: 640 },
      items: 2,
      partialVisibilityGutter: 20,
    },
    mobile: {
      breakpoint: { max: 640, min: 0 },
      items: 1,
      partialVisibilityGutter: 10,
    },
  };

  // Format date function for better display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString("bn-BD", options);
  };

  // Get category color
  const getCategoryColor = (category) => {
    return category?.color || "#3B82F6";
  };

  if (loading) {
    return (
      <div className="container mx-auto py-16">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
        </div>
      </div>
    );
  }

  if (activities?.length === 0) {
    return (
      <div className="container mx-auto py-16">
        <div className="flex justify-center items-center h-64">
          <p className="text-lg text-gray-500">No recent activities found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 md:py-16 px-4 sm:px-6 lg:px-8 mt-20">
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
          সাম্প্রতিক কার্যক্রম
        </h2>
        <div className="w-24 h-1 bg-secondary mx-auto mb-4"></div>
        <p className="text-gray-600 max-w-2xl mx-auto">
          আমাদের সাম্প্রতিক প্রকল্প ও কার্যক্রমসমূহ, যা আপনাদের সহযোগিতায় সম্ভব
          হয়েছে
        </p>
        <Link
          to="/activities"
          className="inline-flex mt-5 items-center gap-2 bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
        >
          সকল কার্যক্রম দেখুন
          <ArrowRight size={20} />
        </Link>
      </div>

      <Carousel
        swipeable={true}
        draggable={true}
        showDots={true}
        responsive={responsive}
        autoPlay={activities.length > 3}
        ssr={true}
        infinite={activities.length > 3}
        autoPlaySpeed={3000}
        keyBoardControl={true}
        customTransition="all 500ms ease"
        transitionDuration={500}
        containerClass="pb-12"
        removeArrowOnDeviceType={["tablet", "mobile"]}
        dotListClass="custom-dot-list-style"
        itemClass="px-3 py-2"
        renderDotsOutside={true}
      >
        {activities?.map((activity) => (
          <div key={activity._id} className="h-full">
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 h-full flex flex-col border border-gray-100">
              <div className="relative overflow-hidden group">
                <img
                  className="h-56 w-full object-cover transition-transform duration-500 group-hover:scale-110"
                  src={`${baseUrl}${activity?.imageUrl}`}
                  alt={activity.title}
                />
                <div className="absolute bottom-0 left-0 bg-gradient-to-t from-black/70 to-transparent w-full h-1/3"></div>
                {/* Featured Badge */}
                {activity.featured && (
                  <div className="absolute top-3 left-3">
                    <div className="bg-yellow-500 text-white px-2 py-1 rounded-full flex items-center text-xs font-medium">
                      <Star size={12} className="mr-1 fill-current" />
                      বিশেষ
                    </div>
                  </div>
                )}

                {/* Activity Date */}
                {activity.activityDate && (
                  <div className="absolute top-3 right-3 bg-secondary text-white px-3 py-1 rounded-full flex items-center text-xs font-medium">
                    <Calendar size={14} className="mr-1" />
                    <span>{formatDate(activity.activityDate)}</span>
                  </div>
                )}

                {/* Category Badge */}
                {activity.category && (
                  <div className="absolute bottom-3 left-3">
                    <span
                      className="px-2 py-1 rounded-full text-white text-xs font-medium"
                      style={{
                        backgroundColor: getCategoryColor(activity.category),
                      }}
                    >
                      {activity.category.name}
                    </span>
                  </div>
                )}
              </div>

              <div className="p-5 flex-grow flex flex-col">
                <h3 className="text-xl font-bold text-gray-800 mb-3 line-clamp-2">
                  {activity.title}
                </h3>
                <p className="text-gray-600 mb-4 line-clamp-3 flex-grow">
                  {activity.shortDescription}
                </p>

                {/* Additional Info */}
                <div className="space-y-2 mb-4">
                  {activity.location && (
                    <div className="flex items-center text-gray-500 text-sm">
                      <MapPin size={14} className="mr-2" />
                      <span>{activity.location}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center text-gray-500">
                      <Eye size={14} className="mr-1" />
                      <span>{activity.views || 0} বার দেখা হয়েছে</span>
                    </div>

                    {activity.participants > 0 && (
                      <div className="flex items-center text-gray-500">
                        <Users size={14} className="mr-1" />
                        <span>{activity.participants} জন</span>
                      </div>
                    )}
                  </div>
                </div>

                <Link
                  to={`/activities/${activity._id}`}
                  className="mt-auto bg-primary hover:bg-secondary/90 text-white py-2 px-6 rounded-lg flex items-center justify-center gap-2 transition-all duration-200 font-medium"
                >
                  বিস্তারিত জানুন
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        ))}
      </Carousel>
    </div>
  );
};

export default RecentActivities;
